import { TESTIMONIALS } from "@/lib/content";

/**
 * TestimonialsStrip — editorial pullquote row.
 *
 * Renders 3-6 anonymized client testimonials in a Ralph Lauren-inspired
 * layout: cormorant italic quote, small-caps attribution, hairline dividers.
 * Attribution is intentionally profession + region rather than a name to
 * comply with Texas Disciplinary Rules of Professional Conduct 7.02(a)(4)
 * and to preserve client confidentiality.
 *
 * Props:
 *   variant — "ivory" (light bg) or "linen" (warm off-white bg)
 *   kicker  — small-caps label above the section
 *   heading — display headline
 *   subhead — small italic line under the heading (optional)
 *   limit   — number of testimonials to show (default 6)
 */
export default function TestimonialsStrip({
  variant = "linen",
  kicker = "In Their Own Words",
  heading = "The counsel our clients keep.",
  subhead = "Attribution held in confidence, per the Texas Rules of Professional Conduct.",
  limit = 6,
}) {
  const bg = variant === "linen" ? "bg-linen" : "bg-ivory";
  const items = TESTIMONIALS.slice(0, limit);

  return (
    <section className={`${bg} py-24 md:py-32`} data-testid="testimonials-strip">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <div className="kicker">{kicker}</div>
            <h2 className="mt-6 font-display text-5xl md:text-6xl leading-[0.98]">
              {heading.split(".").map((part, i, arr) => (
                <span key={i}>
                  {part}
                  {i < arr.length - 1 && "."}
                  {i === arr.length - 2 && <br />}
                </span>
              ))}
            </h2>
          </div>
          {subhead && (
            <p className="lg:col-span-5 lg:col-start-8 text-slate2 font-cormorant italic text-lg md:text-xl leading-relaxed self-end max-w-md">
              {subhead}
            </p>
          )}
        </div>

        <ul
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-14 gap-y-14"
          data-testid="testimonials-list"
        >
          {items.map((t, i) => (
            <li
              key={i}
              className="hairline-top pt-8"
              data-testid={`testimonial-${i}`}
            >
              <div className="font-cormorant italic text-2xl md:text-[1.65rem] text-navy leading-[1.28]">
                &ldquo;{t.quote}&rdquo;
              </div>
              <div className="mt-8 text-[10px] uppercase tracking-[0.32em] text-brass">
                {t.attribution}
              </div>
              <div className="mt-2 text-sm text-slate2 font-cormorant italic">
                {t.region} &middot; {t.stage}
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-16 hairline-top pt-8 flex flex-wrap items-center justify-between gap-6">
          <p className="font-cormorant italic text-lg md:text-xl text-slate2 max-w-2xl leading-snug">
            Prior results do not guarantee a similar outcome. Every matter is
            evaluated on its own facts.
          </p>
          <a
            href="/intake"
            className="btn-brass"
            data-testid="testimonials-cta"
          >
            Request a Conference &rarr;
          </a>
        </div>
      </div>
    </section>
  );
}
